import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'; // 改為 HashRouter
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import PetCalculator from './components/LoveCalculator';
import DateCalculator from './components/DateCalculator';
import Tips from './components/Tips';
import SponsorSection from './components/SponsorSection';
import FAQSection from './components/FAQSection';
import PetKnowledgeBase from './components/PetKnowledgeBase'; // 新增
import Footer from './components/Footer';
import BackgroundPaws from './components/BackgroundHearts';
import SEOHead from './components/SEOHead';
import ScrollToTop from './components/ScrollToTop';
import { useLanguage } from './hooks/useLanguage';
import { useDarkMode } from './hooks/useDarkMode';
import { useSEO } from './hooks/useSEO';
import { parseUrlParams } from './utils/shareUtils';
import './App.css';

const AboutUs = lazy(() => import('./pages/AboutUs'));
const Features = lazy(() => import('./pages/Features'));
const Help = lazy(() => import('./pages/Help'));
const Contact = lazy(() => import('./pages/Contact'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));

function App() {
  const [activeTab, setActiveTab] = useState('pet');
  const { language, setLanguage } = useLanguage();
  const { isDark } = useDarkMode();
  const [calculatorData, setCalculatorData] = useState({});

  useEffect(() => {
    // 修正 HashRouter 下的參數抓取邏輯
    const hash = window.location.hash;
    const search = hash.includes('?') ? hash.split('?')[1] : window.location.search;
    const params = new URLSearchParams(search);
    
    if (params.get('lang')) setLanguage(params.get('lang'));
    if (params.get('tab') === 'standard') setActiveTab('standard');
    
    // 初始化數據抓取
    setCalculatorData({
      petType: params.get('petType') || 'dog',
      anniversaryType: params.get('type') || 'adoption'
    });
  }, []);

  const handleCalculatorDataUpdate = (data) => setCalculatorData(data);

  const { title, description, structuredData } = useSEO({ language, activeTab, calculatorData });

  const canonicalUrl = `https://petdays.homy.hk/#${activeTab === 'pet' ? '/' : '/?tab=standard'}`;

  const LoadingFallback = () => (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-love-500"></div>
    </div>
  );

  return (
    <Router>
      <ScrollToTop />
      <div className={`min-h-screen relative bg-love-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors duration-300 ${isDark ? 'dark' : ''}`}>
        <SEOHead
          title={title}
          description={description}
          language={language}
          canonicalUrl={canonicalUrl}
          structuredData={structuredData}
          activeTab={activeTab}
          anniversaryType={calculatorData?.anniversaryType}
        />

        <BackgroundPaws />

        <main className="max-w-3xl mx-auto p-4 md:p-6 relative z-10">
          <header>
            <Header language={language} setLanguage={setLanguage} />
          </header>

          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              <Route path="/" element={
                <div className="space-y-6">
                  <motion.div
                    className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden relative z-10"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <nav className="bg-gradient-to-r from-love-50 to-secondary-50 dark:from-gray-750 dark:to-gray-800 border-b border-love-100 dark:border-gray-700">
                      <div className="flex px-6">
                        <button
                          onClick={() => setActiveTab('pet')}
                          className={`py-4 px-4 text-center font-medium text-sm flex-1 transition-all ${
                            activeTab === 'pet' ? 'text-love-500 border-b-2 border-love-500' : 'text-gray-500'
                          }`}
                        >
                          {language === 'zh' ? '我的寵物' : 'My Pet'}
                        </button>
                        <button
                          onClick={() => setActiveTab('standard')}
                          className={`py-4 px-4 text-center font-medium text-sm flex-1 transition-all ${
                            activeTab === 'standard' ? 'text-love-500 border-b-2 border-love-500' : 'text-gray-500'
                          }`}
                        >
                          {language === 'zh' ? '日期計算' : 'Date Calculator'}
                        </button>
                      </div>
                    </nav>

                    <AnimatePresence mode="wait">
                      {activeTab === 'pet' ? (
                        <motion.div key="pet" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                          <PetCalculator language={language} onDataUpdate={handleCalculatorDataUpdate} />
                        </motion.div>
                      ) : (
                        <motion.div key="standard" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                          <DateCalculator language={language} onDataUpdate={handleCalculatorDataUpdate} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                  
                  {/* 動態知識庫 - 根據寵物類型顯示不同內容，餵養 AI 爬蟲 */}
                  <PetKnowledgeBase language={language} petType={calculatorData?.petType} />
                  
                  <FAQSection language={language} />
                </div>
              } />
              <Route path="/about" element={<AboutUs language={language} />} />
              <Route path="/features" element={<Features language={language} />} />
              <Route path="/help" element={<Help language={language} />} />
              <Route path="/contact" element={<Contact language={language} />} />
              <Route path="/privacy" element={<PrivacyPolicy language={language} />} />
              <Route path="/terms" element={<TermsOfService language={language} />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Suspense>

          <Tips language={language} />
          <SponsorSection language={language} />
          <Footer language={language} />
        </main>
      </div>
    </Router>
  );
}

export default App;